/**
 * Authentication Service Worker Manager
 *
 * A+ Security Feature: Manages the background service worker for token refresh.
 * This keeps sessions alive even when all tabs are in the background or minimized.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'

let serviceWorkerRegistration: ServiceWorkerRegistration | null = null
let isInitialized = false
// Concurrent callers race against the boolean guard — the first call's
// awaited `navigator.serviceWorker.register` hasn't completed by the time
// the second call reads `isInitialized=false`. Cache the in-flight promise
// so all callers await the same registration.
let initPromise: Promise<boolean> | null = null

interface ServiceWorkerMessage {
  type: 'TOKEN_REFRESHED' | 'SESSION_EXPIRED'
  payload: {
    user?: any
    access_token?: string
    expires_in?: number
  }
}

type MessageHandler = (message: ServiceWorkerMessage) => void

const messageHandlers: Set<MessageHandler> = new Set()

// Fired when a new build's worker has installed and is waiting to take over.
// App.vue subscribes to show a "new version — reload" toast. Kept separate
// from the auth message bus above because it's a PWA lifecycle concern, not a
// session event.
type UpdateHandler = () => void
const updateHandlers: Set<UpdateHandler> = new Set()
let waitingWorker: ServiceWorker | null = null

/**
 * Register and initialize the authentication service worker
 */
export async function initAuthServiceWorker(): Promise<boolean> {
  if (isInitialized) {
    return true
  }
  if (initPromise) {
    // A previous concurrent caller is already registering — share its outcome.
    return initPromise
  }

  if (!('serviceWorker' in navigator)) {
    if (import.meta.env.DEV) {
      console.warn('[Auth SW Manager] Service workers not supported')
    }
    return false
  }

  initPromise = (async () => {
    try {
      // Register the unified worker (PWA precache + background token refresh).
      // This is the SW emitted by vite-plugin-pwa from src/sw.ts; it replaced
      // the old /auth-sw.js. Only ONE worker may control scope "/", so the two
      // concerns share this single file and this single registration.
      serviceWorkerRegistration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      })

      if (import.meta.env.DEV) {
        console.warn('[Auth SW Manager] Service worker registered')
      }

      // Watch for an updated build installing in the background. When it
      // finishes installing while a controller is already active, the new
      // worker is "waiting" — surface that so the UI can prompt a reload.
      watchForUpdates(serviceWorkerRegistration)

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready

      // Initialize the service worker with API URL
      sendToServiceWorker({
        type: 'INIT',
        payload: {
          apiBaseUrl: API_BASE_URL
        }
      })

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)

      isInitialized = true
      return true
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[Auth SW Manager] Failed to register service worker:', error)
      }
      initPromise = null // allow retry on transient failure
      return false
    }
  })()

  return initPromise
}

/**
 * Update the service worker with current session info
 */
export function updateSessionInServiceWorker(tokenExpiresAt: number | null, startRefreshTimer = true): void {
  if (!isInitialized) {
    return
  }

  sendToServiceWorker({
    type: 'UPDATE_SESSION',
    payload: {
      tokenExpiresAt,
      startRefreshTimer
    }
  })
}

/**
 * Clear session in service worker (e.g., on logout)
 */
export function clearSessionInServiceWorker(): void {
  if (!isInitialized) {
    return
  }

  sendToServiceWorker({
    type: 'CLEAR_SESSION',
    payload: {}
  })
}

/**
 * Stop the service worker refresh timer
 */
export function stopServiceWorker(): void {
  if (!isInitialized) {
    return
  }

  sendToServiceWorker({
    type: 'STOP',
    payload: {}
  })
}

/**
 * Subscribe to "a new app version is ready" events. Returns an unsubscribe fn.
 * If a waiting worker already exists at subscribe time, the handler fires
 * immediately so a late subscriber doesn't miss the update.
 */
export function onServiceWorkerUpdate(handler: UpdateHandler): () => void {
  updateHandlers.add(handler)
  if (waitingWorker) {
    handler()
  }
  return () => {
    updateHandlers.delete(handler)
  }
}

/**
 * Apply a pending update: tell the waiting worker to take over, then reload
 * once it does. No-op if nothing is waiting.
 */
export function applyServiceWorkerUpdate(): void {
  if (!waitingWorker) {
    return
  }
  // When the new worker activates it becomes the controller; reload then so
  // the page is served by the fresh shell. Guard against double-reload.
  let reloaded = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return
    reloaded = true
    window.location.reload()
  })
  waitingWorker.postMessage({ type: 'SKIP_WAITING' })
}

/**
 * Wire updatefound -> statechange to detect a freshly installed worker that is
 * waiting behind the current controller.
 */
function watchForUpdates(registration: ServiceWorkerRegistration): void {
  // A build may already be waiting from a previous visit.
  if (registration.waiting && navigator.serviceWorker.controller) {
    waitingWorker = registration.waiting
    notifyUpdateHandlers()
  }

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing
    if (!installing) return

    installing.addEventListener('statechange', () => {
      // 'installed' + an existing controller == an update (not first install).
      if (installing.state === 'installed' && navigator.serviceWorker.controller) {
        waitingWorker = installing
        notifyUpdateHandlers()
      }
    })
  })
}

function notifyUpdateHandlers(): void {
  for (const handler of updateHandlers) {
    try {
      handler()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[Auth SW Manager] Error in update handler:', error)
      }
    }
  }
}

/**
 * Add a handler for service worker messages
 */
export function addServiceWorkerMessageHandler(handler: MessageHandler): () => void {
  messageHandlers.add(handler)

  // Return unsubscribe function
  return () => {
    messageHandlers.delete(handler)
  }
}

/**
 * Unregister the service worker completely
 */
export async function unregisterAuthServiceWorker(): Promise<boolean> {
  if (!serviceWorkerRegistration) {
    return true
  }

  try {
    await serviceWorkerRegistration.unregister()
    serviceWorkerRegistration = null
    isInitialized = false
    return true
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[Auth SW Manager] Failed to unregister service worker:', error)
    }
    return false
  }
}

// Internal helpers

function sendToServiceWorker(message: { type: string; payload: any }): void {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message)
  }
}

function handleServiceWorkerMessage(event: MessageEvent): void {
  const message = event.data as ServiceWorkerMessage

  // Notify all registered handlers
  for (const handler of messageHandlers) {
    try {
      handler(message)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[Auth SW Manager] Error in message handler:', error)
      }
    }
  }
}
